import { useContext, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, CircularProgress, IconButton } from "@mui/material";
import { ArrowBackIosNew, ArrowForwardIos } from "@mui/icons-material";

import CustomText from "../components/CustomText";
import DoctorCard from "./DoctorCard";
import { ScreenContext } from "../context/ScreenContext";
import { useDoctorsDirectory } from "../utils/useDoctorsDirectory";

const CarouselDoctors = ({
  cards = [],
  colorIcon,
  directorio,
  loadingDoctors: loadingDoctorsProp,
}) => {
  const { isMobile } = useContext(ScreenContext);
  const navigate = useNavigate();
  const { doctors: sharedDoctors, loading: sharedLoading } =
    useDoctorsDirectory();
  const [startIndex, setStartIndex] = useState(0);
  const itemsPerPage = isMobile ? 1 : 3;

  /*  DOCTORES */
  const resolvedCards = useMemo(() => {
    return cards?.length > 0 ? cards : sharedDoctors || [];
  }, [cards, sharedDoctors]);

  const loadingDoctors = Boolean(loadingDoctorsProp ?? sharedLoading);

  const total = resolvedCards.length;

  /* DETALLE DEL DOCTOR */

  const handleDoctorDetails = (doctor) => {
    if (!doctor) return;

    const doctorId = doctor.uid ?? doctor.id ?? doctor.cedula;

    if (!doctorId) return;

    navigate(`/detalles-doctor?doctor=${encodeURIComponent(doctorId)}`, {
      state: {
        doctor,
      },
    });
  };

  /* SIGUIENTE */
  const handleNext = () => {
    if (!total) return;

    setStartIndex((prevIndex) => {
      const nextIndex = prevIndex + itemsPerPage;
      if (nextIndex >= total) {
        return 0;
      }
      return nextIndex;
    });
  };

  /* ANTERIOR */
  const handlePrev = () => {
    if (!total) return;

    setStartIndex((prevIndex) => {
      const previousIndex = prevIndex - itemsPerPage;

      if (previousIndex < 0) {
        return Math.floor((total - 1) / itemsPerPage) * itemsPerPage;
      }

      return previousIndex;
    });
  };

  /* DOCTORES VISIBLES  */
  const visibleDoctors = useMemo(() => {
    if (!total) return [];

    return resolvedCards.slice(startIndex, startIndex + itemsPerPage);
  }, [resolvedCards, startIndex, itemsPerPage, total]);

  if (loadingDoctors) {
    return (
      <Box sx={design.LoadingState}>
        <CircularProgress size={25} />
        <CustomText sx={design.LoadingText}>Cargando doctores...</CustomText>
      </Box>
    );
  }

  if (!total) {
    return (
      <Box sx={design.EmptyState}>
        <CustomText>No se encontraron médicos con esos filtros.</CustomText>
      </Box>
    );
  }

  return (
    <Box sx={design.Container}>
      <IconButton
        onClick={handlePrev}
        aria-label="Médicos anteriores"
        sx={design.Arrow}
      >
        <ArrowBackIosNew
          sx={{
            fontSize: {
              xs: 20,
              md: 25,
            },
            color: colorIcon || "#2349D0",
          }}
        />
      </IconButton>
      <Box sx={design.CardsContainer}>
        {visibleDoctors.map((doc, index) => (
          <DoctorCard
            key={doc.uid || doc.id || doc.cedula || index}
            doctor={doc}
            colorIcon={colorIcon}
            onClick={() => handleDoctorDetails(doc)}
          />
        ))}
      </Box>
      <IconButton
        onClick={handleNext}
        aria-label="Médicos siguientes"
        sx={design.Arrow}
      >
        <ArrowForwardIos
          sx={{
            fontSize: {
              xs: 20,
              md: 25,
            },
            color: colorIcon || "#2349D0",
          }}
        />
      </IconButton>
    </Box>
  );
};

export default CarouselDoctors;

const design = {
  Container: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: {
      xs: "10px 0",
      md: "20px 0",
    },
  },
  CardsContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: {
      xs: 0,
      md: 7,
    },
    overflow: "visible",
  },
  Arrow: {
    width: {
      xs: 35,
      md: 45,
    },
    height: {
      xs: 35,
      md: 45,
    },
    flexShrink: 0,
    "&:hover": {
      backgroundColor: "rgba(35, 73, 208, 0.08)",
    },
  },
  LoadingState: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 1,
    padding: 5,
  },
  LoadingText: {
    fontFamily: "Nunito",
    color: "#666",
    fontSize: 15,
  },
  EmptyState: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 5,
  },
};
